import React, { useState, useEffect } from 'react';
import { Card, Spinner, Form, Row, Col, Table, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';

// Inline CSS for custom background colors
const customStyles = `
  .bg-light-success { background-color: #d1f2d1; }
  .bg-light-warning { background-color: #fff3cd; }
  .bg-light-primary { background-color: #cce7ff; }
  .bg-light-secondary { background-color: #f8f9fa; }
`;

interface Daire {
  daireID: number;
  daireNo: string;
  takipNo: string;
  sozlesmeTakipNo?: string;
  projeID: number;
  blokID: number;
  katID: number;
  cepheID: number;
  manzaraID: number;
  daireTipID: number;
  daireDurumu: string;
  yataySira: number;
  listeFiyati?: number;
  netM2?: number;
  brutM2?: number;
  paraBirimiID?: number;
  aciklama?: string;
  proje: {
    projeID: number;
    projeAdi: string;
    takipNo?: string;
  };
  blok: {
    blokID: number;
    blokAdi: string;
  };
  kat: {
    katID: number;
    katAdi: string;
  };
  cephe: {
    cepheID: number;
    cepheAdi: string;
  };
  manzara: {
    manzaraID: number;
    manzaraAdi: string;
  };
  daireTipi: {
    daireTipID: number;
    tipAdi?: string;
    tip?: string;
  };
}

interface Proje {
  projeID: number;
  projeAdi: string;
}

interface Blok {
  blokID: number;
  blokAdi: string;
  projeID: number;
}

interface SunumBelge {
  sunumBelgeId: number;
  takipNo: string;
  aciklama: string;
  sunumBelgeTuru: string;
  sunumBelgeAdi: string;
}

const API_BASE_URL = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

const DairelerTablo: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [daireler, setDaireler] = useState<Daire[]>([]);
  const [filteredDaireler, setFilteredDaireler] = useState<Daire[]>([]);
  const [sozlesmeMusterileri, setSozlesmeMusterileri] = useState<Record<number, string>>({});
  const [modalDaire, setModalDaire] = useState<Daire | null>(null);
  const [modalDaireOzellikler, setModalDaireOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [modalProjeOzellikler, setModalProjeOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [galeriGorseller, setGaleriGorseller] = useState<SunumBelge[]>([]);
  const [galeriBelgeler, setGaleriBelgeler] = useState<SunumBelge[]>([]);
  const [galeriVideolar, setGaleriVideolar] = useState<SunumBelge[]>([]);
  const [galeriSlideIndex, setGaleriSlideIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState<SunumBelge | null>(null);
  
  // Filter state'leri
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [bloklar, setBloklar] = useState<Blok[]>([]);
  const [filters, setFilters] = useState({
    projeID: '',
    blokID: ''
  });

  useEffect(() => {
    fetchDaireler();
    fetchProjeler();
    fetchSozlesmeMusterileri();
  }, []);

  // Filtreler değiştiğinde otomatik uygula
  useEffect(() => {
    applyFilters();
  }, [filters, daireler]);

  // Modal açıldığında daire/proje özelliklerini ve galeriyi yükle
  useEffect(() => {
    if (modalDaire?.takipNo) {
      axiosInstance.get(`/api/DaireOzellikler/daire/${modalDaire.takipNo}`)
        .then(res => setModalDaireOzellikler(res.data))
        .catch(() => setModalDaireOzellikler([]));
      axiosInstance.get(`/api/SunumBelgeleri/takip/${modalDaire.takipNo}`)
        .then(res => {
          const daireBelgeler: SunumBelge[] = res.data || [];
          if (modalDaire?.proje?.takipNo) {
            axiosInstance.get(`/api/SunumBelgeleri/takip/${modalDaire.proje.takipNo}`)
              .then(res2 => {
                const projeBelgeler: SunumBelge[] = res2.data || [];
                const daireGorseller = daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel');
                const daireBelgeleri = daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge');
                const daireVideolar = daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video');
                setGaleriGorseller(daireGorseller.length > 0 ? daireGorseller : projeBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
                setGaleriBelgeler(daireBelgeleri.length > 0 ? daireBelgeleri : projeBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
                setGaleriVideolar(daireVideolar.length > 0 ? daireVideolar : projeBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
                setGaleriSlideIndex(0);
              }).catch(() => {
                setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
                setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
                setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
                setGaleriSlideIndex(0);
              });
          } else {
            setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
            setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
            setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
            setGaleriSlideIndex(0);
          }
        }).catch(() => {
          setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]); setGaleriSlideIndex(0);
        });
    } else {
      setModalDaireOzellikler([]);
      setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]);
    }
    if (modalDaire?.projeID) {
      axiosInstance.get(`/api/ProjeOzellikler/proje-id/${modalDaire.projeID}`)
        .then(res => setModalProjeOzellikler(res.data))
        .catch(() => {
          if (modalDaire?.proje?.takipNo) {
            axiosInstance.get(`/api/ProjeOzellikler/proje/${modalDaire.proje.takipNo}`)
              .then(res2 => setModalProjeOzellikler(res2.data))
              .catch(() => setModalProjeOzellikler([]));
          } else {
            setModalProjeOzellikler([]);
          }
        });
    } else {
      setModalProjeOzellikler([]);
    }
  }, [modalDaire]);

  const fetchDaireler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Daireler?aktifMi=true');
      console.log('Daire verisi örneği:', response.data[0]); // Debug için
      setDaireler(response.data);
    } catch (error) {
      console.error('Daireler yüklenirken hata oluştu:', error);
      toast.error('Daireler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Projeler yüklenirken hata oluştu:', error);
    }
  };

  const fetchBloklar = async (projeID: string) => {
    if (!projeID) {
      setBloklar([]);
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/api/Bloklar/proje/${projeID}`);
      setBloklar(response.data);
    } catch (error) {
      console.error('Bloklar yüklenirken hata oluştu:', error);
      setBloklar([]);
    }
  };

  const fetchSozlesmeMusterileri = async () => {
    try {
      const response = await axiosInstance.get('/api/Sozlesmeler?aktifMi=true');
      const musteriMap: Record<number, string> = {};
      (response.data as any[]).forEach(s => {
        if (s.daire?.daireID && s.cari?.adiSoyadi) {
          musteriMap[s.daire.daireID] = s.cari.adiSoyadi;
        }
      });
      setSozlesmeMusterileri(musteriMap);
    } catch {
      // sessiz hata
    }
  };

  // Filtreleme fonksiyonu
  const applyFilters = () => {
    let filtered = [...daireler];

    // Proje filtresi (Tümü seçeneği yok, seçim zorunlu)
    if (filters.projeID) {
      filtered = filtered.filter(daire => daire.projeID.toString() === filters.projeID);
    } else {
      // Proje seçilmemişse hiçbir daire gösterme
      filtered = [];
    }

    // Blok filtresi (Tümü seçeneği yok, seçim zorunlu)
    if (filters.blokID) {
      filtered = filtered.filter(daire => daire.blokID.toString() === filters.blokID);
    } else if (filters.projeID) {
      // Proje seçili ama blok seçilmemişse hiçbir daire gösterme
      filtered = [];
    }

    setFilteredDaireler(filtered);
  };

  // Daireleri kata göre grupla
  const getGroupedDaireler = () => {
    const grouped: { [katAdi: string]: Daire[] } = {};
    
    filteredDaireler.forEach(daire => {
      const katAdi = daire.kat?.katAdi || 'Belirsiz';
      if (!grouped[katAdi]) {
        grouped[katAdi] = [];
      }
      grouped[katAdi].push(daire);
    });

    // Kat adlarını sırala (sayısal sıralama için)
    const sortedKatlar = Object.keys(grouped).sort((a, b) => {
      // Eğer kat adları sayısal ise sayısal sıralama yap
      const aNum = parseInt(a.replace(/\D/g, ''));
      const bNum = parseInt(b.replace(/\D/g, ''));
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return bNum - aNum; // Büyükten küçüğe (üst katlar önce)
      }
      
      return a.localeCompare(b, 'tr');
    });

    return sortedKatlar.map(katAdi => ({
      katAdi,
      daireler: grouped[katAdi].sort((a, b) => (a.yataySira || 0) - (b.yataySira || 0))
    }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));

    // Proje değiştiğinde blokları yükle ve blok seçimini sıfırla
    if (key === 'projeID') {
      setFilters(prev => ({ ...prev, blokID: '' }));
      fetchBloklar(value);
    }
  };

  const DURUM_CONFIG: Record<string, { border: string; bg: string; badgeBg: string; badgeColor: string; dot: string; label: string }> = {
    'Satışta':    { border: '#22c55e', bg: '#f0fdf4', badgeBg: '#dcfce7', badgeColor: '#15803d', dot: '#22c55e', label: 'Satışta' },
    'Rezerve':    { border: '#f59e0b', bg: '#fffbeb', badgeBg: '#fef3c7', badgeColor: '#b45309', dot: '#f59e0b', label: 'Rezerve' },
    'Sözleşme':   { border: '#3b82f6', bg: '#eff6ff', badgeBg: '#dbeafe', badgeColor: '#1d4ed8', dot: '#3b82f6', label: 'Sözleşme' },
    'ArsaSahibi': { border: '#94a3b8', bg: '#f8fafc', badgeBg: '#f1f5f9', badgeColor: '#475569', dot: '#94a3b8', label: 'Arsa Sahibi' },
    'Taşeron':    { border: '#0ea5e9', bg: '#f0f9ff', badgeBg: '#e0f2fe', badgeColor: '#0369a1', dot: '#0ea5e9', label: 'Taşeron' },
  };
  const getDurumConfig = (durum: string) =>
    DURUM_CONFIG[durum] || { border: '#94a3b8', bg: '#f8fafc', badgeBg: '#f1f5f9', badgeColor: '#475569', dot: '#94a3b8', label: durum };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 360, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            border: '3px solid #e2e8f0', borderTopColor: '#3b82f6',
            animation: 'dt-spin 0.8s linear infinite', margin: '0 auto 14px',
          }} />
          <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Yükleniyor…</span>
        </div>
        <style>{`@keyframes dt-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const groupedFloors = getGroupedDaireler();
  const LEGEND = [
    { label: 'Satışta',     dot: '#22c55e' },
    { label: 'Rezerve',     dot: '#f59e0b' },
    { label: 'Sözleşme',    dot: '#3b82f6' },
    { label: 'Arsa Sahibi', dot: '#8b5cf6' },
    { label: 'Taşeron',     dot: '#0ea5e9' },
  ];

  return (
    <div style={{ background: '#F8F9FA', minHeight: '100vh', padding: '28px 32px', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @keyframes dt-spin { to { transform: rotate(360deg); } }
        @keyframes dt-fadeup { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .dt-select {
          appearance: none;
          -webkit-appearance: none;
          padding: 9px 36px 9px 16px;
          border-radius: 9999px;
          border: 1.5px solid #e2e8f0;
          background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 12px center;
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
          cursor: pointer;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          min-width: 180px;
        }
        .dt-select:hover { border-color: #94a3b8; }
        .dt-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
        .dt-select:disabled { background-color: #f8fafc; color: #cbd5e1; cursor: not-allowed; }
        .dt-unit-card {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #f1f5f9;
          box-shadow: 0 1px 4px rgba(15,23,42,0.05);
          padding: 12px 14px 10px 16px;
          position: relative;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
          overflow: hidden;
          min-width: 120px;
          max-width: 150px;
          flex: 0 0 130px;
        }
        .dt-unit-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(15,23,42,0.1);
        }
        .dt-unit-card::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 4px;
          border-radius: 12px 0 0 12px;
        }
        .dt-floor-panel {
          background: #fff;
          border-radius: 16px;
          border: 1.5px solid #f1f5f9;
          box-shadow: 0 1px 4px rgba(15,23,42,0.05);
          padding: 16px 20px;
          display: flex;
          align-items: flex-start;
          gap: 20px;
          animation: dt-fadeup 0.2s ease both;
        }
        .dt-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px;
          border-radius: 9999px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.03em;
          white-space: nowrap;
        }
        .dt-prompt {
          background: #fff;
          border-radius: 16px;
          border: 1.5px solid #f1f5f9;
          box-shadow: 0 1px 4px rgba(15,23,42,0.05);
          padding: 56px 24px;
          text-align: center;
        }
        .dt-dots-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 7px;
          border: none;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.15s;
          z-index: 2;
          padding: 0;
          font-size: 14px;
        }
        .dt-dots-btn:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .dt-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15,23,42,0.45);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: dt-fadein 0.15s ease;
        }
        @keyframes dt-fadein { from { opacity: 0; } to { opacity: 1; } }
        .dt-modal {
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(15,23,42,0.2);
          width: 96%;
          max-width: 720px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: dt-slideup 0.2s ease;
        }
        @keyframes dt-slideup { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .dt-modal-header {
          padding: 20px 24px 14px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
        }
        .dt-modal-body {
          padding: 18px 24px 24px;
          overflow-y: auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        .dt-modal-field {
          background: #f8fafc;
          border-radius: 10px;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .dt-modal-label {
          font-size: 10.5px;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.3px;
        }
        .dt-modal-value {
          font-size: 13.5px;
          font-weight: 700;
          color: #1e293b;
        }
        .dt-close-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.15s;
          flex-shrink: 0;
        }
        .dt-close-btn:hover {
          background: #fef2f2;
          border-color: #fca5a5;
          color: #ef4444;
        }
        .dt-modal-footer {
          padding: 14px 24px;
          border-top: 1px solid #f1f5f9;
          display: flex;
          justify-content: flex-end;
        }
      `}</style>

      {/* ── PAGE HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Proje Yönetimi</h1>
          <p style={{ fontSize: '13.5px', color: '#64748b', margin: '4px 0 0' }}>Aktif projelerinizin ilerleme durumlarını ve satış hedeflerini izleyin.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          {/* View Toggle – icon only segmented control */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
            {/* Kart – INACTIVE */}
            <button
              onClick={() => navigate('/daireler/liste')}
              title="Kart Görünümü"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: 'transparent', color: '#94a3b8',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#475569'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
            >
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
              </svg>
            </button>
            {/* Tablo – ACTIVE */}
            <button
              title="Kat Planı Görünümü"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'default',
                background: '#fff', color: '#cd6906',
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                transition: 'all 0.15s',
              }}
            >
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>
          <div style={{ width: 1, height: 28, background: '#e2e8f0', flexShrink: 0 }} />
          <button
            onClick={() => navigate('/sozlesmeler')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              background: '#fff', color: '#475569',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
              transition: 'border-color 0.15s, background 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = '#fff'; }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Sözleşmeler
          </button>
          <button
            onClick={() => navigate('/satis-sozlesmesi')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 16px', borderRadius: 9, border: 'none',
              background: '#cd6906', color: '#fff',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              cursor: 'pointer', whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(205,105,6,0.3)',
              transition: 'background 0.15s, transform 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#b85d05'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Yeni Sözleşme
          </button>
        </div>
      </div>

      {/* ── FILTER BAR */}
      <div style={{
        background: '#fff', borderRadius: 16,
        border: '1.5px solid #f1f5f9',
        boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
        padding: '16px 24px', marginBottom: 20,
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {/* Proje */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', paddingLeft: 4 }}>Proje</span>
          <select
            className="dt-select"
            value={filters.projeID}
            onChange={e => handleFilterChange('projeID', e.target.value)}
          >
            <option value="">Proje Seçin…</option>
            {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
          </select>
        </div>

        {/* Arrow separator */}
        <svg style={{ color: '#cbd5e1', marginTop: 18 }} width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>

        {/* Blok */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: filters.projeID ? '#94a3b8' : '#d1d5db', letterSpacing: '0.5px', paddingLeft: 4 }}>Blok</span>
          <select
            className="dt-select"
            value={filters.blokID}
            onChange={e => handleFilterChange('blokID', e.target.value)}
            disabled={!filters.projeID}
          >
            <option value="">Blok Seçin…</option>
            {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
          </select>
        </div>

        {/* Spacer + Legend */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          {LEGEND.map(l => (
            <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: l.dot, flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── MAIN CONTENT */}
      {!filters.projeID ? (
        <div className="dt-prompt">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22V12h6v10"/><path d="M8 6h.01M12 6h.01M16 6h.01"/></svg></div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Proje Seçin</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Kat planını görmek için yukarıdan bir proje seçin.</div>
        </div>
      ) : !filters.blokID ? (
        <div className="dt-prompt">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg></div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Blok Seçin</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Görüntülemek istediğiniz bloğu seçin.</div>
        </div>
      ) : filteredDaireler.length === 0 ? (
        <div className="dt-prompt">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg></div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Daire bulunamadı</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Seçilen blokta kayıtlı daire bulunmuyor.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* ── Stats row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            {(['Satışta','Rezerve','Sözleşme','ArsaSahibi','Taşeron'] as const).map(durum => {
              const count = filteredDaireler.filter(d => d.daireDurumu === durum).length;
              if (!count) return null;
              const cfg = getDurumConfig(durum);
              return (
                <div key={durum} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#fff', borderRadius: 10,
                  border: `1.5px solid ${cfg.border}22`,
                  padding: '7px 14px',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: cfg.border, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{count}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>{cfg.label}</span>
                </div>
              );
            })}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#f8fafc', borderRadius: 10,
              border: '1.5px solid #e2e8f0',
              padding: '7px 14px',
              marginLeft: 'auto',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{filteredDaireler.length}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: '#64748b' }}>Toplam Daire</span>
            </div>
          </div>

          {/* ── Floor panels */}
          {groupedFloors.map((katGroup, katIdx) => (
            <div
              key={katGroup.katAdi}
              className="dt-floor-panel"
              style={{ animationDelay: `${katIdx * 0.04}s` }}
            >
              {/* Floor label */}
              <div style={{
                flexShrink: 0, width: 72,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                paddingTop: 4,
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>
                  {katGroup.katAdi.replace(/[^0-9]/g, '') || katGroup.katAdi}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginTop: 2 }}>Kat</div>
                <div style={{ width: 1, background: '#e2e8f0', flex: 1, marginTop: 10, minHeight: 12 }} />
              </div>

              {/* Divider */}
              <div style={{ width: 1, background: '#f1f5f9', alignSelf: 'stretch', flexShrink: 0 }} />

              {/* Unit cards */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, flex: 1 }}>
                {katGroup.daireler.map((daire) => {
                  const cfg = getDurumConfig(daire.daireDurumu);
                  const tipAdi = daire.daireTipi?.tipAdi || daire.daireTipi?.tip;
                  return (
                    <div
                      key={daire.daireID}
                      className="dt-unit-card"
                      style={{ '--accent': cfg.border } as React.CSSProperties}
                      title={`Daire ${daire.daireNo}\nCephe: ${daire.cephe?.cepheAdi || '-'}\nManzara: ${daire.manzara?.manzaraAdi || '-'}\nDurum: ${daire.daireDurumu}${daire.listeFiyati ? '\nFiyat: ' + daire.listeFiyati.toLocaleString('tr-TR') + ' ₺' : ''}${daire.netM2 ? '\nNet: ' + daire.netM2 + ' m²' : ''}`}
                    >
                      {/* 3-dot menu button */}
                      <button
                        className="dt-dots-btn"
                        onClick={(e) => { e.stopPropagation(); setModalDaire(daire); }}
                        title="Daire Detay"
                      >
                        <i className="fas fa-ellipsis-v" />
                      </button>

                      {/* Left accent border via pseudo - set via inline style instead */}
                      <div style={{
                        position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
                        borderRadius: '12px 0 0 12px',
                        background: cfg.border,
                      }} />

                      {/* Unit number */}
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 4 }}>
                        {daire.daireNo}
                        {tipAdi && (
                          <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', marginLeft: 5 }}>({tipAdi})</span>
                        )}
                      </div>

                      {/* Cephe */}
                      {daire.cephe?.cepheAdi && (
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', lineHeight: 1.4, marginBottom: 2 }}>
                          {daire.cephe.cepheAdi}
                        </div>
                      )}
                      {/* Manzara */}
                      {daire.manzara?.manzaraAdi && (
                        <div style={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.3, marginBottom: 6 }}>
                          {daire.manzara.manzaraAdi}
                        </div>
                      )}

                      {/* Status info shown via left accent border colour only – no badge chip */}

                      {/* Müşteri adı */}
                      {['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(daire.daireDurumu) && sozlesmeMusterileri[daire.daireID] && (
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#475569', marginTop: 4, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={sozlesmeMusterileri[daire.daireID]}>
                          👤 {sozlesmeMusterileri[daire.daireID]}
                        </div>
                      )}

                      {/* m² */}
                      {(daire.netM2 || daire.brutM2) && (
                        <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 5, fontVariantNumeric: 'tabular-nums' }}>
                          {daire.netM2 && `${daire.netM2}m²`}
                          {daire.netM2 && daire.brutM2 && ' / '}
                          {daire.brutM2 && `${daire.brutM2}m²`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* ── DAIRE DETAY MODAL */}
      {modalDaire && (() => {
        const cfg = getDurumConfig(modalDaire.daireDurumu);
        const tipAdi = modalDaire.daireTipi?.tipAdi || modalDaire.daireTipi?.tip;
        return (
          <div className="dt-modal-overlay" onClick={() => setModalDaire(null)}>
            <div className="dt-modal" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="dt-modal-header">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 4 }}>
                    {modalDaire.proje?.projeAdi || '—'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.04em' }}>
                      Daire {modalDaire.daireNo}
                    </span>
                    <span className="dt-badge" style={{ background: cfg.badgeBg, color: cfg.badgeColor }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
                      {cfg.label}
                    </span>
                  </div>
                  {modalDaire.takipNo && (
                    <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                      Takip No: <span style={{ fontWeight: 700, color: '#64748b' }}>{modalDaire.takipNo}</span>
                    </div>
                  )}
                </div>
                <button className="dt-close-btn" onClick={() => setModalDaire(null)}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1={18} y1={6} x2={6} y2={18}/><line x1={6} y1={6} x2={18} y2={18}/></svg>
                </button>
              </div>

              {/* Body */}
              <div className="dt-modal-body">
                <div className="dt-modal-field">
                  <span className="dt-modal-label">Blok</span>
                  <span className="dt-modal-value">{modalDaire.blok?.blokAdi || '—'}</span>
                </div>
                <div className="dt-modal-field">
                  <span className="dt-modal-label">Kat</span>
                  <span className="dt-modal-value">{modalDaire.kat?.katAdi || '—'}</span>
                </div>
                <div className="dt-modal-field">
                  <span className="dt-modal-label">Cephe</span>
                  <span className="dt-modal-value">{modalDaire.cephe?.cepheAdi || '—'}</span>
                </div>
                <div className="dt-modal-field">
                  <span className="dt-modal-label">Manzara</span>
                  <span className="dt-modal-value">{modalDaire.manzara?.manzaraAdi || '—'}</span>
                </div>
                {tipAdi && (
                  <div className="dt-modal-field">
                    <span className="dt-modal-label">Daire Tipi</span>
                    <span className="dt-modal-value">{tipAdi}</span>
                  </div>
                )}
                {modalDaire.netM2 != null && (
                  <div className="dt-modal-field">
                    <span className="dt-modal-label">Net m²</span>
                    <span className="dt-modal-value">{modalDaire.netM2} m²</span>
                  </div>
                )}
                {modalDaire.brutM2 != null && (
                  <div className="dt-modal-field">
                    <span className="dt-modal-label">Brüt m²</span>
                    <span className="dt-modal-value">{modalDaire.brutM2} m²</span>
                  </div>
                )}
                {modalDaire.listeFiyati != null && (
                  <div className="dt-modal-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="dt-modal-label">Liste Fiyatı</span>
                    <span className="dt-modal-value" style={{ fontSize: 20, color: '#059669' }}>
                      ₺{modalDaire.listeFiyati.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(modalDaire.daireDurumu) && sozlesmeMusterileri[modalDaire.daireID] && (
                  <div className="dt-modal-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="dt-modal-label">Sözleşme Müşterisi</span>
                    <span className="dt-modal-value" style={{ fontSize: 16, color: '#1e293b' }}>{sozlesmeMusterileri[modalDaire.daireID]}</span>
                  </div>
                )}
                {modalDaire.aciklama && (
                  <div className="dt-modal-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="dt-modal-label">Açıklama</span>
                    <span className="dt-modal-value" style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>{modalDaire.aciklama}</span>
                  </div>
                )}
                {modalDaireOzellikler.length > 0 && (
                  <div style={{ gridColumn: '1 / -1', marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Daire Özellikleri</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {modalDaireOzellikler.map((oz, idx) => (
                        <div key={idx} className="dt-modal-field">
                          <span className="dt-modal-label">{oz.ozellik}</span>
                          <span className="dt-modal-value">{oz.deger}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {modalProjeOzellikler.length > 0 && (
                  <div style={{ gridColumn: '1 / -1', marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Proje Özellikleri</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {modalProjeOzellikler.map((oz, idx) => (
                        <div key={idx} className="dt-modal-field">
                          <span className="dt-modal-label">{oz.ozellik}</span>
                          <span className="dt-modal-value">{oz.deger}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(galeriGorseller.length > 0 || galeriBelgeler.length > 0 || galeriVideolar.length > 0) && (
                  <div style={{ gridColumn: '1 / -1', marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>Galeri</div>
                    {galeriGorseller.length > 0 && (
                      <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#f1f5f9', marginBottom: 12 }}>
                        <img
                          src={`${API_BASE_URL}/api/SunumBelgeleri/download/${galeriGorseller[galeriSlideIndex]?.sunumBelgeId}`}
                          alt={galeriGorseller[galeriSlideIndex]?.aciklama || ''}
                          style={{ width: '100%', maxHeight: 300, objectFit: 'contain', display: 'block' }}
                        />
                        {galeriGorseller[galeriSlideIndex]?.aciklama && (
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', color: '#fff', padding: '18px 14px 8px', fontSize: 12, fontWeight: 600 }}>
                            {galeriGorseller[galeriSlideIndex].aciklama}
                          </div>
                        )}
                        {galeriGorseller.length > 1 && (
                          <>
                            <button onClick={() => setGaleriSlideIndex(i => i <= 0 ? galeriGorseller.length - 1 : i - 1)}
                              style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                            <button onClick={() => setGaleriSlideIndex(i => i >= galeriGorseller.length - 1 ? 0 : i + 1)}
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontSize: 16, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                          </>
                        )}
                        {galeriGorseller.length > 1 && (
                          <div style={{ position: 'absolute', bottom: galeriGorseller[galeriSlideIndex]?.aciklama ? 30 : 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5 }}>
                            {galeriGorseller.map((_, i) => (
                              <span key={i} onClick={() => setGaleriSlideIndex(i)}
                                style={{ width: 8, height: 8, borderRadius: '50%', background: i === galeriSlideIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.7)' }} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {galeriBelgeler.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>📄 Belgeler</div>
                        {galeriBelgeler.map(b => (
                          <div key={b.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 14 }}></i>
                            <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                            <a href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                              <i className="fas fa-download" style={{ marginRight: 3 }}></i>İndir
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                    {galeriVideolar.length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>🎬 Videolar</div>
                        {galeriVideolar.map(v => (
                          <div key={v.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                            onClick={() => setShowVideoModal(v)}>
                            <i className="fas fa-play-circle" style={{ color: '#dc2626', fontSize: 16 }}></i>
                            <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{v.aciklama || 'Video'}</span>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>Oynat →</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="dt-modal-footer">
                <button
                  onClick={() => setModalDaire(null)}
                  style={{
                    padding: '8px 20px', borderRadius: 10,
                    border: '1.5px solid #e2e8f0', background: '#f8fafc',
                    fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {showVideoModal && (
        <div className="dt-modal-overlay" onClick={() => setShowVideoModal(null)} style={{ zIndex: 1060 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#000', borderRadius: 12, maxWidth: 720, width: '90%', overflow: 'hidden', position: 'relative' }}>
            <button onClick={() => setShowVideoModal(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>×</button>
            {showVideoModal.sunumBelgeAdi && (
              <div style={{ width: '100%' }}
                dangerouslySetInnerHTML={{ __html: showVideoModal.sunumBelgeAdi }} />
            )}
            {showVideoModal.aciklama && (
              <div style={{ padding: '8px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{showVideoModal.aciklama}</div>
            )}
          </div>
        </div>
      )}    </div>
  );
};

export default DairelerTablo;